import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * "Complete the set" bundle: tick the items you want, see the running total, add them all in one
 * request. Prices come from the store (data attributes rendered by Liquid); the only saving shown
 * is one the merchant configured, so the total never invents a discount.
 *
 * Adding follows the same event contract as the product form's batch add
 * (assets/product-form.js), so the cart drawer opens and the cart bubble updates as usual.
 */
class ProductBundle extends HTMLElement {
  /** @type {HTMLInputElement[]} */
  inputs = [];

  /** @type {HTMLButtonElement | null} */
  button = null;

  /** @type {HTMLElement | null} */
  totalEl = null;

  /** @type {HTMLElement | null} */
  strikeEl = null;

  /** @type {HTMLElement | null} */
  statusEl = null;

  connectedCallback() {
    this.inputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[data-bundle-item]')]);
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-add]'));
    this.totalEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-bundle-total]'));
    this.strikeEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-bundle-strike]'));
    this.statusEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-bundle-status]'));

    this.addEventListener('change', this.#update);
    this.button?.addEventListener('click', this.#add);
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#update);
    this.button?.removeEventListener('click', this.#add);
  }

  get #checked() {
    return this.inputs.filter((input) => input.checked);
  }

  /** @param {number} cents */
  #money(cents) {
    const formatted = formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'GBP');
    // Match the Liquid `money_without_trailing_zeros` output used elsewhere on the page.
    return formatted.replace(/([.,])00(?=\D*$)/, '');
  }

  #update = () => {
    const checked = this.#checked;
    const sum = checked.reduce((total, input) => total + Number(input.dataset.price || 0), 0);
    const compareSum = checked.reduce(
      (total, input) => total + Math.max(Number(input.dataset.compareAt || 0), Number(input.dataset.price || 0)),
      0
    );
    const discount = checked.length >= 2 ? Number(this.dataset.discount || 0) : 0;
    const total = Math.max(sum - discount, 0);
    const strike = discount > 0 ? sum : compareSum;

    if (this.totalEl) this.totalEl.textContent = this.#money(total);
    if (this.strikeEl) {
      this.strikeEl.textContent = this.#money(strike);
      this.strikeEl.hidden = !(strike > total);
    }

    if (this.button) {
      const count = checked.length;
      const label =
        count === 1
          ? this.dataset.labelOne || 'Add to bag'
          : (this.dataset.labelMany || 'Add {count} items to bag').replace('{count}', String(count));
      this.button.textContent = label;
      this.button.disabled = count === 0;
    }
  };

  /**
   * The main product row follows whatever variant the shopper picked in the buy box.
   * @param {HTMLInputElement} input
   * @returns {string} The variant id, or an empty string when the row has none.
   */
  #variantIdFor(input) {
    if (input.dataset.currentProduct === 'true') {
      const form = document.querySelector(
        `product-form-component[data-product-id="${input.dataset.productId}"] input[name="id"]`
      );
      if (form instanceof HTMLInputElement && form.value) return form.value;
    }
    return input.dataset.variantId || '';
  }

  #add = async () => {
    const items = this.#checked
      .map((input) => ({ variantId: this.#variantIdFor(input), quantity: 1 }))
      .filter((item) => item.variantId !== '');
    if (!items.length || !this.button) return;

    this.button.disabled = true;
    this.#setStatus('');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })),
          sections: sectionIds.join(','),
        }),
      }).then((res) => res.json());

      const cart = await this.#fetchCart();

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'product-bundle', itemCount: items.length },
        });
        this.#setStatus(response.description || response.message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-bundle',
          sourceId: this.id,
          itemCount: items.length,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.#setStatus(this.dataset.errorText || 'Something went wrong. Please try again.');
    } finally {
      this.#update();
    }
  };

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && 'fetchCartData' in cartItems) {
      await customElements.whenDefined('cart-items-component');
      // @ts-ignore - provided by component-cart-items.js
      return cartItems.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());
  }

  /** @param {string} message */
  #setStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.hidden = !message;
  }
}

if (!customElements.get('product-bundle')) {
  customElements.define('product-bundle', ProductBundle);
}
